// =============================================================================
// SIREPPAR - Componente de Carga de Documentos
// =============================================================================

import { useState, useCallback, useRef } from 'react';
import { MAX_ARCHIVO_MB } from '../../utils/archivos';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// =============================================================================
// Tipos
// =============================================================================

interface FileWithPreview extends File {
  preview?: string;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface FileUploadProps {
  onUpload: (files: File[]) => Promise<void>;
  accept?: string;
  maxSize?: number; // en MB
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  showPreview?: boolean;
  label?: string;
  description?: string;
}

// =============================================================================
// Utilidades
// =============================================================================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (type: string | undefined): string => {
  if (!type) return '📎';
  if (type.startsWith('image/')) return '🖼️';
  if (type === 'application/pdf') return '📄';
  if (type.includes('word')) return '📝';
  if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
  return '📎';
};

// =============================================================================
// Componente
// =============================================================================

export default function FileUpload({
  onUpload,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  maxSize = MAX_ARCHIVO_MB,
  maxFiles = 10,
  multiple = true,
  disabled = false,
  showPreview = true,
  label = 'Cargar documentos',
  description = 'Arrastre archivos aquí o haga clic para seleccionar',
}: FileUploadProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Validar tamaño
    if (file.size > maxSize * 1024 * 1024) {
      return `El archivo excede el tamaño máximo de ${maxSize}MB`;
    }
    
    // Validar tipo
    const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    const fileMime = file.type.toLowerCase();
    
    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExt === type;
      }
      if (type.includes('*')) {
        return fileMime.startsWith(type.replace('*', ''));
      }
      return fileMime === type;
    });
    
    if (!isAccepted) {
      return 'Tipo de archivo no permitido';
    }
    
    return null;
  }, [accept, maxSize]);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    
    // Validar cantidad máxima
    if (files.length + fileArray.length > maxFiles) {
      alert(`Solo puede cargar hasta ${maxFiles} archivos`);
      return;
    }
    
    const processedFiles: FileWithPreview[] = fileArray.map(file => {
      const error = validateFile(file);
      const fileWithPreview = file as FileWithPreview;
      fileWithPreview.id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      fileWithPreview.status = error ? 'error' : 'pending';
      fileWithPreview.progress = 0;
      fileWithPreview.error = error || undefined;
      
      // Generar preview para imágenes
      if (showPreview && file.type.startsWith('image/')) {
        fileWithPreview.preview = URL.createObjectURL(file);
      }
      
      return fileWithPreview;
    });
    
    setFiles(prev => [...prev, ...processedFiles]);
  }, [files.length, maxFiles, validateFile, showPreview]);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (!disabled && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [disabled, addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    // Reset input para permitir seleccionar el mismo archivo
    e.target.value = '';
  }, [addFiles]);

  const handleUpload = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;
    
    setIsUploading(true);
    
    // Actualizar estado a uploading
    setFiles(prev => prev.map(f => 
      f.status === 'pending' ? { ...f, status: 'uploading' as const } : f
    ));
    
    try {
      await onUpload(pendingFiles);
      
      // Marcar como exitosos
      setFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'success' as const, progress: 100 } : f
      ));
    } catch (error) {
      // Marcar como error
      setFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { 
          ...f, 
          status: 'error' as const, 
          error: 'Error al cargar el archivo' 
        } : f
      ));
    } finally {
      setIsUploading(false);
    }
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const hasErrors = files.some(f => f.status === 'error');

  return (
    <div className="space-y-4">
      {/* Zona de drop — se oculta cuando ya hay un archivo y no se permiten múltiples */}
      {(!(!multiple && files.length > 0)) && <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled}
          className="hidden"
        />
        
        <CloudArrowUpIcon className={`mx-auto h-12 w-12 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
        
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
          <p className="mt-2 text-xs text-gray-400">
            Formatos: {accept} • Máximo {maxSize}MB por archivo
          </p>
        </div>
      </div>}

      {/* Lista de archivos */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">
              {files.length} archivo(s) seleccionado(s)
            </span>
            {pendingCount > 0 && (
              <button
                onClick={() => setFiles([])}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Eliminar todos
              </button>
            )}
          </div>
          
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {files.map((file) => (
              <li key={file.id} className="flex items-center gap-4 p-3 bg-white">
                {/* Preview o icono */}
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <span className="text-2xl">{getFileIcon(file.type)}</span>
                  )}
                </div>
                
                {/* Información */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  
                  {/* Barra de progreso */}
                  {file.status === 'uploading' && (
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                  
                  {/* Error */}
                  {file.error && (
                    <p className="mt-1 text-xs text-red-600">{file.error}</p>
                  )}
                </div>
                
                {/* Estado/Acciones */}
                <div className="flex-shrink-0">
                  {file.status === 'success' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  )}
                  {file.status === 'error' && (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  {(file.status === 'pending' || file.status === 'error') && (
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                  {file.status === 'uploading' && (
                    <svg className="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Botón de carga */}
      {pendingCount > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={isUploading || hasErrors}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Cargando...
              </span>
            ) : (
              `Cargar ${pendingCount} archivo(s)`
            )}
          </button>
        </div>
      )}
    </div>
  );
}
